import React, { useState } from 'react';
import { PrintDialog } from '@frontend/assets';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Stepper, Text, TextLink } from '@frontend/design-system';
import { insuranceReportTrackingIds } from '../trackingIds';

type EligibilityReportWritebackErrorContainerProps = {
  PrintableReport: JSX.Element;
  PMSName: string;
  closeModal: () => void;
  setWritebackErrorMessage: (value: string) => void;
  errorMessage?: string;
};

export const helpCenterURL =
  'https://www.weavehelp.com/hc/en-us/articles/4410657422363-Verify-Dental-Insurance-Information';

export const EligibilityReportWritebackErrorContainer = React.memo(
  ({
    PMSName,
    closeModal,
    setWritebackErrorMessage,
    errorMessage = '',
    PrintableReport,
  }: EligibilityReportWritebackErrorContainerProps) => {
    const { t } = useTranslation('insuranceEligibilityReport');
    const [isPrintDialogVisible, setIsPrintDialogVisible] = useState<boolean>(false);

    const openHelpCenter = () => {
      window.open(helpCenterURL, '_blank');
    };

    const getErrorMessageContent = (): JSX.Element => {
      return (
        <>
          <Text css={{ marginTop: theme.spacing(1) }} weight='bold'>
            {t('Error: {{errorMessage}}', { errorMessage })}
          </Text>
          <Text css={{ margin: theme.spacing(2, 0, 3, 0) }}>
            {t(
              "Weave can't write the PDF to the document center for {{PMSName}} right now. Follow these steps to get the PDF into your PMS's document center",
              { PMSName }
            )}
          </Text>
        </>
      );
    };

    const handlePrint = () => {
      setIsPrintDialogVisible(true);
    };

    const handleOnClose = () => {
      closeModal();
      setWritebackErrorMessage('');
    };

    return (
      <>
        {getErrorMessageContent()}
        <Stepper maxWidth={800} shouldScrollToCard={false}>
          <Stepper.Card>
            <Stepper.Title>{t('Print or save a PDF of the eligibility report using the button below')}</Stepper.Title>
            <Stepper.Content>
              <PrintDialog show={isPrintDialogVisible} onClose={() => setIsPrintDialogVisible(false)} landscape>
                {PrintableReport}
              </PrintDialog>
              <Button
                variant='primary'
                css={{ width: 'fit-content' }}
                trackingId={insuranceReportTrackingIds.writebackModalStepperSaveToPMSBtn}
                onClick={handlePrint}
              >
                {t('Print/Save Eligibility Report')}
              </Button>
            </Stepper.Content>
            <Stepper.ButtonBar>
              <Stepper.NextButton
                isValid
                trackingId={insuranceReportTrackingIds.writebackModalStepperPrintBtnInErrorStep}
              ></Stepper.NextButton>
            </Stepper.ButtonBar>
          </Stepper.Card>
          <Stepper.Card>
            <Stepper.Title>{t("Upload the PDF into your PMS's document center")}</Stepper.Title>
            <Stepper.Content>
              <Trans t={t}>
                <Text>
                  You can find the PDF you generated from Weave Insurance Verification in your Downloads folder on your
                  computer&apos;s workstation. From there you may upload the PDF directly into the file directory of
                  your practice management system. For help,{' '}
                  <TextLink
                    onClick={openHelpCenter}
                    trackingId={insuranceReportTrackingIds.writebackModalClickHereLink}
                  >
                    click here
                  </TextLink>
                </Text>
              </Trans>
            </Stepper.Content>
            <Stepper.ButtonBar>
              <Stepper.NextButton
                isValid
                trackingId={insuranceReportTrackingIds.writebackModalStepperPrintBtnInErrorStep}
              ></Stepper.NextButton>
            </Stepper.ButtonBar>
          </Stepper.Card>
          <Stepper.Card>
            <Stepper.Title>{'Validate the document is appearing correctly in your PMS'}</Stepper.Title>
            <Stepper.Content>
              <Trans t={t}>
                <Text>
                  We want to ensure that the document has uploaded successfully and that you&apos;re able to preview the
                  PDF in your practice management system. For help,{' '}
                  <TextLink
                    onClick={openHelpCenter}
                    trackingId={insuranceReportTrackingIds.writebackModalStepperHelpArticleLinkBtn}
                  >
                    click here
                  </TextLink>
                </Text>
              </Trans>
            </Stepper.Content>
            <Stepper.ButtonBar>
              <Stepper.NextButton
                isValid
                trackingId={insuranceReportTrackingIds.writebackModalStepperNextBtn}
              ></Stepper.NextButton>
            </Stepper.ButtonBar>
          </Stepper.Card>
          <Stepper.Card>
            <Stepper.Title>{"You're all set!"}</Stepper.Title>
            <Stepper.Content>
              <Button
                variant='primary'
                css={{ width: 'fit-content' }}
                trackingId={insuranceReportTrackingIds.writebackModalStepperCloseBtnInErrorStep}
                onClick={handleOnClose}
              >
                {t('Close')}
              </Button>
            </Stepper.Content>
          </Stepper.Card>
        </Stepper>
      </>
    );
  }
);

EligibilityReportWritebackErrorContainer.displayName = 'EligibilityReportWritebackErrorContainer';
